/**
 * CANONICAL QR CONFIGURATION — SINGLE SOURCE OF TRUTH.
 *
 * Both the browser (components/StyledQRCode) and the server-side renderer in
 * sigalmark-be build their options here. Do not re-implement this logic on
 * either side: identical output depends on identical input, and a duplicated
 * copy will drift silently.
 *
 * This module must stay dependency-free and DOM-free so Node can import it.
 *
 * `type` is deliberately 'svg'. The historical `default` style set no type, so
 * qr-code-styling fell back to canvas and the browser painted raster pixels —
 * which cannot be byte-compared against a server render.
 */

export type QrTints = {
  tintElements?: string | null;
  tintText?: string | null;
  tintBackground?: string | null;
};

export type QrConfigInput = QrTints & {
  url: string;
  size?: number;
  styleKey?: string;
  image?: string;
};

type StyleOptions = {
  dotsOptions?: { type?: string; color?: string };
  cornersSquareOptions?: { type?: string; color?: string };
  cornersDotOptions?: { type?: string; color?: string };
  imageOptions?: Record<string, unknown>;
  backgroundOptions?: Record<string, unknown>;
  shape?: string;
  margin?: number;
};

/** Mirrors utils/qrStyles' `default`, with an explicit svg type. */
export const DEFAULT_STYLE: StyleOptions = {
  dotsOptions: { type: 'rounded', color: '#000000' },
  backgroundOptions: {
    color: '#ffffff',
    marginColor: '#ffffff',
    outlineColor: '#000000',
    innerLineColor: '#000000',
    outlineWidth: 2,
    innerLineWidth: 1,
    marginTextFont: 'Arial, sans-serif',
    marginTextSize: 10,
    marginTextColor: '#000000',
  },
};

const clean = (v?: string | null) => {
  const s = (v ?? '').trim();
  return s ? s : undefined;
};

/**
 * Builds the exact options object passed to QRCodeStyling. Key order is fixed
 * because the serialised SVG is compared byte-for-byte in the fixture test.
 */
export function buildQrConfig(
  input: QrConfigInput,
  style: StyleOptions = DEFAULT_STYLE,
): Record<string, unknown> {
  const { url, size = 150, tintElements, tintText, tintBackground } = input;
  const ink = clean(tintElements);
  const text = clean(tintText);
  const bg = clean(tintBackground);

  return {
    ...style,
    type: 'svg',
    width: size,
    height: size,
    data: url,
    dotsOptions: { ...style.dotsOptions, color: ink ?? style.dotsOptions?.color },
    cornersSquareOptions: {
      ...style.cornersSquareOptions,
      color: ink ?? style.cornersSquareOptions?.color,
    },
    cornersDotOptions: {
      ...style.cornersDotOptions,
      color: ink ?? style.cornersDotOptions?.color,
    },
    backgroundOptions: {
      ...style.backgroundOptions,
      color: bg ?? (style.backgroundOptions?.color as string | undefined),
      marginColor: ink ?? (style.backgroundOptions?.marginColor as string | undefined),
      marginTextColor: text ?? (style.backgroundOptions?.marginTextColor as string | undefined),
    },
  };
}
